'use client';
import IconCircleInfo from '@mnd-frontend/icons/components/CircleInfo';
import {
  RADIUS_LG,
  SIZING_1,
  SIZING_2,
  SPACING_1,
  SPACING_2,
  SPACING_5,
} from '@mnd-frontend/ui/shared/tokens';
import type { ReactNode } from 'react';
import styled from 'styled-components';
import { Button } from '../Button';
import { Text } from '../Text';
import media from '../utils/media';

const Wrapper = styled.aside`
  background-color: ${props => props.theme.colorBackgroundSystemInfoDefault};
  display: flex;
  gap: ${SPACING_5};
  justify-content: center;
  min-height: ${props => props.theme.sizingSystemInfoHeight};
  position: relative;
  z-index: 2;
  max-width: 100%;
  overflow: hidden;
  margin-top: var(--header-height-mobile);
  ${media.greaterThan('navigationBreakpoint')} {
    margin-top: unset;
    border-top-right-radius: ${RADIUS_LG};
    border-top-left-radius: ${RADIUS_LG};
  }
`;

const TextPart = styled.div`
  display: flex;
  gap: ${SPACING_1};
  align-items: center;
  color: ${props => props.theme.colorTextBodyOnDark};

  > :first-child {
    flex-shrink: 0;
  }

  > p {
    padding: ${SIZING_2} 0;
    ${media.lessThan('phablet')} {
      padding: ${SIZING_1} 0;
    }
  }
`;

const Body = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 0.375rem 0 0.75rem; //Match with menu
  width: 100%;
  gap: ${SPACING_5};

  ${media.lessThan('tablet')} {
    gap: ${SPACING_2};
  }
  ${media.lessThan('phablet')} {
    gap: 0;
    flex-direction: column;
  }
`;

export const SystemInfo = ({
  children,
  action,
}: {
  children: ReactNode;
  action?: {
    text: string;
    onClick: () => void;
    loading?: boolean;
  };
}) => {
  return (
    <Wrapper>
      <Body>
        <TextPart>
          <IconCircleInfo $size="xs" $color="colorIconsOnDark" />
          <Text as="p" variant="body">
            {children}
          </Text>
        </TextPart>
        {action && (
          <Button
            onDark
            type="button"
            configuration="text"
            onClick={action.onClick}
            loading={action.loading}
          >
            {action.text}
          </Button>
        )}
      </Body>
    </Wrapper>
  );
};
